/**
 * CompanyDetailPage — `/admin/companies/:id` ISA / MSA detail.
 *
 * Matches the design artboard
 * (`.design-incoming/admin-20260522/source/admin.jsx` CompanyDetailPage):
 *
 *   - Breadcrumb "‹ Companies".
 *   - Hero card (brand-tinted radial gradient): type icon + TypeChip +
 *     status badge + name + meta line (/slug · region · currency ·
 *     Created date), with Import / Settings / Change status actions
 *     (Copy-to-new when closed), over a stat band (ISA: Properties ·
 *     Users · Linked MSAs · Region; MSA: Member ISAs · Users · Region).
 *   - Closed banner when the system is closed.
 *   - Sticky section-pill tabs (ISA: Overview · Linked MSAs · Settings ·
 *     Users · Imports · Audit log; MSA swaps Linked MSAs → Member ISAs,
 *     no Imports tab).
 *   - Tab bodies: Overview (Identity + System/Group settings 2-col),
 *     the relationship tab (parent MSA / member ISAs), Settings
 *     (General + Lifecycle), Users, Imports, Audit log.
 *
 * Earlier this surface was built to `COMPANIES_SPEC.md` with a
 * disclosure-card pattern; that spec had drifted from the artboard, so
 * it's rebuilt to the hero + sticky-tab shape the reviewed screenshot
 * shows.
 *
 * `useCompanyDetail` returns a discriminated union; Phase-1 default is
 * `'not_wired'` so the live route surfaces the coming-soon panel until
 * `GET /admin/companies/:id` ships. Tests drive the page via
 * `companyOverride`.
 */

import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, Icon } from '@sparta/ui-kit';
import { IfAdminPermission } from '../../../shared/auth/IfAdminPermission.js';
import {
  SystemStatusBadge,
  type SystemStatus,
} from '../_components/SystemStatusBadge.js';
import { TypeChip } from '../_components/TypeChip.js';
import { type CompanyRow } from './CompaniesListPage.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';

// ── Types ──────────────────────────────────────────────────────

interface CompanyDetailCommon
  extends Pick<
    CompanyRow,
    | 'id'
    | 'name'
    | 'slug'
    | 'region'
    | 'system_status'
    | 'created_at'
    | 'properties_count'
    | 'users_count'
  > {
  /** Display currency (e.g. "USD"). */
  readonly currency: string;
  /** ISO timestamp; only set when system_status === 'closed'. */
  readonly closed_at: string | null;
}

export interface IsaCompanyDetail extends CompanyDetailCommon {
  readonly type: 'ISA';
  /** IANA time zone (e.g. America/Phoenix). */
  readonly time_zone: string;
  readonly parent_msa_id: string | null;
  readonly parent_msa_name: string | null;
}

export interface MsaMemberIsaSummary {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly system_status: SystemStatus;
}

export interface MsaCompanyDetail extends CompanyDetailCommon {
  readonly type: 'MSA';
  readonly member_isas: ReadonlyArray<MsaMemberIsaSummary>;
}

export type CompanyDetail = IsaCompanyDetail | MsaCompanyDetail;

export type CompanyDetailResult =
  | { readonly status: 'not_wired' }
  | { readonly status: 'loading' }
  | { readonly status: 'not_found' }
  | { readonly status: 'found'; readonly company: CompanyDetail };

function useCompanyDetail(_id: string | undefined): CompanyDetailResult {
  return React.useMemo<CompanyDetailResult>(() => ({ status: 'not_wired' }), []);
}

// ── Page ───────────────────────────────────────────────────────

export interface CompanyDetailPageProps {
  /** Test injection — overrides `useCompanyDetail` (object = found,
   *  `null` = not_found, unset = the hook's not_wired default). */
  readonly companyOverride?: CompanyDetail | null;
  readonly compact?: boolean;
}

export function CompanyDetailPage(
  props: CompanyDetailPageProps = {},
): React.ReactElement {
  return (
    <IfAdminPermission permission="companies:read" fallback={<NotPermittedPanel />}>
      <CompanyDetailBody {...props} />
    </IfAdminPermission>
  );
}

// ── Panels ─────────────────────────────────────────────────────

function CenteredPanel({
  headingId,
  heading,
  children,
  busy,
}: {
  readonly headingId?: string;
  readonly heading?: string;
  readonly children: React.ReactNode;
  readonly busy?: boolean;
}): React.ReactElement {
  return (
    <section
      aria-labelledby={headingId}
      aria-busy={busy ? 'true' : undefined}
      aria-live={busy ? 'polite' : undefined}
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      {heading !== undefined ? (
        <h1 id={headingId} className="text-xl font-semibold" style={{ color: 'var(--fg-primary)' }}>
          {heading}
        </h1>
      ) : null}
      {children}
    </section>
  );
}

function BackToCompanies(): React.ReactElement {
  return (
    <div style={{ marginTop: '16px' }}>
      <Button asChild variant="secondary">
        <Link to="/admin/companies">Back to Companies</Link>
      </Button>
    </div>
  );
}

function NotPermittedPanel(): React.ReactElement {
  return (
    <CenteredPanel headingId="company-detail-not-permitted-heading" heading="Not permitted">
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        You don't have access to view company details. Ask a platform
        administrator if you need this access.
      </p>
      <BackToCompanies />
    </CenteredPanel>
  );
}

function NotFoundPanel({ id }: { readonly id: string | undefined }): React.ReactElement {
  return (
    <CenteredPanel headingId="company-detail-not-found-heading" heading="Company not found">
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        {id === undefined ? 'No company id was provided.' : `No company found for id "${id}".`}
      </p>
      <BackToCompanies />
    </CenteredPanel>
  );
}

function BackendNotWiredPanel({ id }: { readonly id: string | undefined }): React.ReactElement {
  return (
    <CenteredPanel headingId="company-detail-not-wired-heading" heading="Company detail — coming soon">
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        The backend endpoint that powers this page hasn't shipped yet. Once{' '}
        <code>GET /admin/companies/:id</code> lands, this route renders the full
        company detail
        {id !== undefined ? <> for company <code>{id}</code></> : null}.
      </p>
      <BackToCompanies />
    </CenteredPanel>
  );
}

function LoadingPanel(): React.ReactElement {
  return (
    <CenteredPanel busy>
      <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Loading company detail…
      </p>
    </CenteredPanel>
  );
}

// ── Body ───────────────────────────────────────────────────────

function tabsForType(type: 'ISA' | 'MSA'): readonly string[] {
  return type === 'ISA'
    ? ['Overview', 'Linked MSAs', 'Settings', 'Users', 'Imports', 'Audit log']
    : ['Overview', 'Member ISAs', 'Settings', 'Users', 'Audit log'];
}

function CompanyDetailBody(props: CompanyDetailPageProps): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const compact = useCompactMode(props.compact);
  const remote = useCompanyDetail(id);

  const result = React.useMemo<CompanyDetailResult>(() => {
    if (props.companyOverride === undefined) return remote;
    if (props.companyOverride === null) return { status: 'not_found' as const };
    return { status: 'found' as const, company: props.companyOverride };
  }, [props.companyOverride, remote]);

  const company = result.status === 'found' ? result.company : null;
  const [active, setActive] = React.useState<string>('Overview');

  if (result.status === 'not_wired') return <BackendNotWiredPanel id={id} />;
  if (result.status === 'loading') return <LoadingPanel />;
  if (result.status === 'not_found' || company === null) return <NotFoundPanel id={id} />;

  const isClosed = company.system_status === 'closed';
  const tabs = tabsForType(company.type);

  return (
    <article
      aria-labelledby="company-detail-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '1280px', padding: compact ? '20px 16px 80px' : '28px 48px 80px' }}
    >
      <Link
        to="/admin/companies"
        className="inline-flex items-center no-underline"
        style={{ gap: '6px', fontSize: '13px', color: 'var(--fg-tertiary)', marginBottom: '14px' }}
      >
        <Icon name="chevLeft" size={13} aria-hidden /> Companies
      </Link>

      {/* Hero */}
      <Card padding="none">
        <div className="relative overflow-hidden" style={{ borderRadius: 'var(--radius-2xl)' }}>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 700px 280px at 92% -10%, color-mix(in oklab, var(--brand-500) 14%, transparent), transparent 70%)',
            }}
          />
          <div
            className="relative flex flex-wrap items-start"
            style={{ gap: compact ? '16px' : '24px', padding: compact ? '20px 20px 0' : '28px 32px 0' }}
          >
            <div
              className="inline-flex shrink-0 items-center justify-center"
              style={{
                width: '64px',
                height: '64px',
                borderRadius: 'var(--radius-xl)',
                background:
                  company.type === 'MSA'
                    ? 'color-mix(in oklab, var(--brand-500) 16%, var(--bg-surface))'
                    : 'var(--bg-sunken)',
                color: company.type === 'MSA' ? 'var(--brand-700)' : 'var(--fg-secondary)',
              }}
            >
              <Icon name={company.type === 'MSA' ? 'grid' : 'building'} size={28} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center" style={{ gap: '10px', marginBottom: '6px' }}>
                <TypeChip type={company.type} />
                <SystemStatusBadge status={company.system_status} />
                {isClosed && company.closed_at !== null ? (
                  <span style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}>
                    Closed {company.closed_at}
                  </span>
                ) : null}
              </div>
              <h1
                id="company-detail-heading"
                className="m-0 font-semibold"
                style={{ fontSize: compact ? '22px' : '28px', letterSpacing: '-0.4px', color: 'var(--fg-primary)' }}
              >
                {company.name}
              </h1>
              <div
                className="flex flex-wrap items-center"
                style={{ marginTop: '8px', gap: '12px', fontSize: '14px', color: 'var(--fg-secondary)' }}
              >
                <span className="font-mono">/{company.slug}</span>
                <Dot />
                <span>{company.region}</span>
                <Dot />
                <span>{company.currency}</span>
                <Dot />
                <span>Created {company.created_at}</span>
              </div>
            </div>
            {!compact ? (
              <div className="flex shrink-0" style={{ gap: '8px' }}>
                {!isClosed ? (
                  <>
                    <IfAdminPermission permission="imports:run">
                      <Button asChild variant="secondary">
                        <Link to="/admin/imports">
                          <Icon name="arrowUp" size={16} aria-hidden /> Import
                        </Link>
                      </Button>
                    </IfAdminPermission>
                    <Button type="button" variant="secondary">
                      <Icon name="settings" size={16} aria-hidden /> Settings
                    </Button>
                    <IfAdminPermission permission="companies:transition_status">
                      <Button type="button" variant="primary">
                        <Icon name="bolt" size={16} aria-hidden /> Change status
                      </Button>
                    </IfAdminPermission>
                  </>
                ) : (
                  <Button type="button" variant="secondary">
                    Copy to new {company.type}
                  </Button>
                )}
              </div>
            ) : null}
          </div>
          {/* Stat band */}
          <div
            className="relative grid"
            style={{
              marginTop: '24px',
              gridTemplateColumns: compact ? '1fr 1fr' : `repeat(${company.type === 'ISA' ? 4 : 3}, 1fr)`,
              borderTop: '1px solid var(--border-subtle)',
            }}
          >
            {company.type === 'ISA' ? (
              <>
                <Stat label="Properties" value={String(company.properties_count)} />
                <Stat label="Users" value={String(company.users_count)} bordered />
                <Stat label="Linked MSAs" value={String(company.parent_msa_id === null ? 0 : 1)} bordered />
                {!compact ? <Stat label="Region" value={company.region} bordered /> : null}
              </>
            ) : (
              <>
                <Stat label="Member ISAs" value={String(company.member_isas.length)} />
                <Stat label="Users" value={String(company.users_count)} bordered />
                {!compact ? <Stat label="Region" value={company.region} bordered /> : null}
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Closed banner */}
      {isClosed ? (
        <div
          role="alert"
          className="flex items-center"
          style={{
            gap: '12px',
            padding: '12px 16px',
            marginTop: '16px',
            background: 'color-mix(in oklab, var(--danger-500) 8%, var(--bg-surface))',
            border: '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
            borderRadius: 'var(--radius-xl)',
            color: 'var(--danger-700)',
          }}
        >
          <Icon name="lock" size={16} aria-hidden />
          <div className="flex-1" style={{ fontSize: '13px' }}>
            <strong>This system is closed.</strong> Read-only for internal staff.
            Client users can no longer sign in. Operational data is preserved for
            audit and compliance.
          </div>
        </div>
      ) : null}

      {/* Section pill */}
      <div className="sticky z-20 flex" style={{ top: '88px', marginTop: '20px', marginBottom: '20px' }}>
        <div
          role="tablist"
          aria-label="Company sections"
          className="flex"
          style={{
            gap: '2px',
            padding: '4px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-full)',
            boxShadow: 'var(--shadow-sm)',
            maxWidth: '100%',
            overflowX: 'auto',
          }}
        >
          {tabs.map((t) => {
            const isActive = active === t;
            return (
              // Hand-rolled <button>: a WAI-ARIA tab in a sticky pill
              // tablist; kit Button has no tab variant.
              // eslint-disable-next-line @sparta/no-hand-rolled-button
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(t)}
                className="font-semibold"
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  background: isActive ? 'var(--fg-primary)' : 'transparent',
                  color: isActive ? 'var(--bg-surface)' : 'var(--fg-secondary)',
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab body */}
      {active === 'Overview' ? <OverviewTab company={company} compact={compact} /> : null}
      {active === 'Linked MSAs' && company.type === 'ISA' ? <LinkedMsaTab company={company} /> : null}
      {active === 'Member ISAs' && company.type === 'MSA' ? <MemberIsaTab company={company} /> : null}
      {active === 'Settings' ? <SettingsTab company={company} compact={compact} /> : null}
      {active === 'Users' ? <UsersTab company={company} isClosed={isClosed} /> : null}
      {active === 'Imports' ? <ImportsTab isClosed={isClosed} /> : null}
      {active === 'Audit log' ? <AuditTab /> : null}
    </article>
  );
}

// ── Tabs ───────────────────────────────────────────────────────

function OverviewTab({
  company,
  compact,
}: {
  readonly company: CompanyDetail;
  readonly compact: boolean;
}): React.ReactElement {
  return (
    <div className="grid" style={{ gap: '16px', gridTemplateColumns: compact ? '1fr' : '1fr 1fr' }}>
      <Card padding="lg">
        <SectionHeading>Identity</SectionHeading>
        <DetailRow label="Display name" value={company.name} />
        <DetailRow label="Slug" value={`/${company.slug}`} mono />
        <DetailRow label="Type" value={company.type === 'MSA' ? 'Multi-System Account' : 'Individual System Account'} />
        <DetailRow label="Status" value={<SystemStatusBadge status={company.system_status} />} />
        <DetailRow label="Created" value={company.created_at} />
        {company.system_status === 'closed' ? (
          <DetailRow label="Closed at" value={company.closed_at ?? '—'} />
        ) : null}
      </Card>
      <Card padding="lg">
        <SectionHeading>{company.type === 'MSA' ? 'Group' : 'System'} settings</SectionHeading>
        {company.type === 'ISA' ? (
          <>
            <DetailRow label="Currency" value={company.currency} />
            <DetailRow label="Region" value={company.region} />
            <DetailRow label="Time zone" value={company.time_zone} />
            <DetailRow label="Properties" value={String(company.properties_count)} />
            <DetailRow label="Active users" value={String(company.users_count)} />
          </>
        ) : (
          <>
            <DetailRow label="Member ISAs" value={String(company.member_isas.length)} />
            <DetailRow label="Aggregated properties" value={String(company.properties_count)} />
            <DetailRow label="Aggregated users" value={String(company.users_count)} />
            <DetailRow label="Region" value={company.region} />
          </>
        )}
      </Card>
    </div>
  );
}

function LinkedMsaTab({ company }: { readonly company: IsaCompanyDetail }): React.ReactElement {
  if (company.parent_msa_id === null) {
    return (
      <Card padding="lg">
        <div className="text-center" style={{ padding: '16px' }}>
          <p className="m-0 font-semibold" style={{ fontSize: '14px', color: 'var(--fg-primary)' }}>
            Not in any MSA
          </p>
          <p className="m-0" style={{ fontSize: '13px', color: 'var(--fg-tertiary)', marginTop: '4px' }}>
            Standalone ISA. Add to an MSA to enable group navigation.
          </p>
        </div>
      </Card>
    );
  }
  return (
    <Card padding="none">
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <SectionHeading inline>Member of 1 MSA</SectionHeading>
      </div>
      <div className="flex items-center" style={{ gap: '10px', padding: '14px 20px' }}>
        <TypeChip type="MSA" />
        <Link
          to={`/admin/companies/${company.parent_msa_id}`}
          className="font-semibold no-underline"
          style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
        >
          {company.parent_msa_name ?? company.parent_msa_id}
        </Link>
      </div>
    </Card>
  );
}

function MemberIsaTab({ company }: { readonly company: MsaCompanyDetail }): React.ReactElement {
  return (
    <Card padding="none">
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <SectionHeading inline>
          {company.member_isas.length} member ISA{company.member_isas.length === 1 ? '' : 's'}
        </SectionHeading>
      </div>
      {company.member_isas.map((isa, idx) => (
        <div
          key={isa.id}
          className="flex items-center"
          style={{
            gap: '10px',
            padding: '14px 20px',
            borderTop: idx === 0 ? 'none' : '1px solid var(--border-subtle)',
          }}
        >
          <TypeChip type="ISA" />
          <div className="min-w-0 flex-1">
            <Link
              to={`/admin/companies/${isa.id}`}
              className="block font-semibold no-underline"
              style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
            >
              {isa.name}
            </Link>
            <span className="font-mono" style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}>
              /{isa.slug}
            </span>
          </div>
          <SystemStatusBadge status={isa.system_status} size="sm" showTooltip={false} />
        </div>
      ))}
    </Card>
  );
}

function SettingsTab({
  company,
  compact,
}: {
  readonly company: CompanyDetail;
  readonly compact: boolean;
}): React.ReactElement {
  const isClosed = company.system_status === 'closed';
  return (
    <div className="grid" style={{ gap: '16px', gridTemplateColumns: compact ? '1fr' : '1fr 1fr' }}>
      <Card padding="lg">
        <SectionHeading>General</SectionHeading>
        <DetailRow label="Display name" value={company.name} />
        <DetailRow label="Slug" value={`/${company.slug}`} mono />
        {company.type === 'ISA' ? (
          <>
            <DetailRow label="Currency" value={company.currency} />
            <DetailRow label="Region" value={company.region} />
          </>
        ) : null}
      </Card>
      <Card padding="lg">
        <SectionHeading>Lifecycle</SectionHeading>
        <p className="m-0" style={{ fontSize: '13px', color: 'var(--fg-tertiary)', marginBottom: '16px' }}>
          {company.type === 'ISA' ? 'Testing → Active → Closed' : 'Active → Closed'}. Closed is terminal.
        </p>
        <DetailRow label="Current status" value={<SystemStatusBadge status={company.system_status} />} />
        {!isClosed ? (
          <IfAdminPermission permission="companies:transition_status">
            <div className="flex flex-wrap" style={{ gap: '8px', marginTop: '16px' }}>
              {company.system_status === 'testing' ? (
                <Button type="button" variant="primary">
                  <Icon name="check" size={16} aria-hidden /> Activate
                </Button>
              ) : null}
              <Button type="button" variant="secondary">
                <Icon name="lock" size={16} aria-hidden /> Close system
              </Button>
            </div>
          </IfAdminPermission>
        ) : (
          <div style={{ marginTop: '16px' }}>
            <Button type="button" variant="secondary">Copy to new {company.type}</Button>
          </div>
        )}
      </Card>
    </div>
  );
}

function UsersTab({
  company,
  isClosed,
}: {
  readonly company: CompanyDetail;
  readonly isClosed: boolean;
}): React.ReactElement {
  return (
    <Card padding="none">
      <div className="flex items-center" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <h2 className="m-0 flex-1 font-semibold" style={{ fontSize: '14px', color: 'var(--fg-primary)' }}>
          {company.users_count} user{company.users_count === 1 ? '' : 's'}
        </h2>
      </div>
      <p className="m-0 text-center" style={{ padding: '32px', fontSize: '13px', color: 'var(--fg-tertiary)' }}>
        {isClosed
          ? 'This system is closed — user management is read-only.'
          : 'Client-side user management lives in the PMS users area.'}
      </p>
    </Card>
  );
}

function ImportsTab({ isClosed }: { readonly isClosed: boolean }): React.ReactElement {
  return (
    <Card padding="none">
      <div className="flex items-center" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <h2 className="m-0 flex-1 font-semibold" style={{ fontSize: '14px', color: 'var(--fg-primary)' }}>
          Recent imports
        </h2>
        {!isClosed ? (
          <IfAdminPermission permission="imports:run">
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin/imports">Run import</Link>
            </Button>
          </IfAdminPermission>
        ) : null}
      </div>
      <p className="m-0 text-center" style={{ padding: '32px', fontSize: '13px', color: 'var(--fg-tertiary)' }}>
        No imports for this company yet.
      </p>
    </Card>
  );
}

function AuditTab(): React.ReactElement {
  return (
    <Card padding="none">
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <h2 className="m-0 font-semibold" style={{ fontSize: '14px', color: 'var(--fg-primary)' }}>
          Audit log
        </h2>
      </div>
      <p className="m-0 text-center" style={{ padding: '32px', fontSize: '13px', color: 'var(--fg-tertiary)' }}>
        The audit feed ships with <code>GET /admin/companies/:id/audit</code>.
      </p>
    </Card>
  );
}

// ── Small presentational helpers ───────────────────────────────

function Dot(): React.ReactElement {
  return (
    <span aria-hidden="true" style={{ color: 'var(--fg-tertiary)' }}>
      ·
    </span>
  );
}

function SectionHeading({
  children,
  inline,
}: {
  readonly children: React.ReactNode;
  readonly inline?: boolean;
}): React.ReactElement {
  return (
    <h2
      className="m-0 font-bold"
      style={{
        fontSize: inline ? '14px' : '16px',
        color: 'var(--fg-primary)',
        marginBottom: inline ? '0' : '16px',
      }}
    >
      {children}
    </h2>
  );
}

function Stat({
  label,
  value,
  bordered,
}: {
  readonly label: string;
  readonly value: string;
  readonly bordered?: boolean;
}): React.ReactElement {
  return (
    <div style={{ padding: '18px 24px 22px', borderLeft: bordered ? '1px solid var(--border-subtle)' : undefined }}>
      <div
        className="font-bold uppercase"
        style={{ fontSize: '11px', letterSpacing: '0.8px', color: 'var(--fg-tertiary)', marginBottom: '6px' }}
      >
        {label}
      </div>
      <div className="font-semibold" style={{ fontSize: '22px', letterSpacing: '-0.4px', color: 'var(--fg-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly mono?: boolean;
}): React.ReactElement {
  return (
    <div className="flex items-center" style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <div
        className="shrink-0 font-bold uppercase"
        style={{ width: '160px', fontSize: '11px', letterSpacing: '0.6px', color: 'var(--fg-tertiary)' }}
      >
        {label}
      </div>
      <div className={mono ? 'flex-1 font-mono' : 'flex-1'} style={{ fontSize: '14px', color: 'var(--fg-primary)' }}>
        {value}
      </div>
    </div>
  );
}
